import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  KeyboardArrowDown as ArrowDownIcon,
  InfoOutlined as InfoIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { TableVirtuoso } from 'react-virtuoso';
import { authGet } from '../../../services/apiService';
import { DEEP_GREEN, MUTED, INK, INCIDENT_ACCENT, WARN_ORANGE, L2_ACCENT, NEUTRAL_SURFACE, TOP_CUSTOMERS_ACCENT } from '../colorTokens';
import { ALERT_STATE_STYLES, ALL_ALERT_STATES, CLAMP_INNER_SX, STATUS_COLORS } from './monitorConstants';
import { compareNewestFirst, docCategory, monitorEventsUrl, notifyGroupKey, TRIGGERED_RANGES, triggeredInRange, useMonitorStats } from './monitorStats';
import { DEFAULT_DONUT_TAB, DONUT_ROWS, DonutCardRows, selectedDonutLabel } from './DonutCards';
import {
  CreatorsPopup,
  DuplicateGroupsPopup,
  MonitorDetailPopup,
  NotifyGroupsPopup,
  OverlapGroupPopup,
  RecentlyNotifiedPopup,
  RunbooksPopup,
  TypeReferencePopup
} from './MonitorPopups';
import { MultiCheckSelect, ALL_FILTER, isAllFilter, filterAllows } from '../MultiCheckSelect';

// Filter-bar chips use the same dark green as the selected-card ring (not the theme's near-black primary).
const CHIP_SX = { bgcolor: TOP_CUSTOMERS_ACCENT, color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } } };
// MUI only deletes a focused chip on Backspace/Delete; also let Enter/Space remove it, like clicking its X.
const chipRemoveKeys = (remove) => (remove ? {
  onKeyDown: (e) => {
    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
    e.preventDefault();
    remove(e);
  }
} : {});
const OUTLINED_CHIP_SX = { color: TOP_CUSTOMERS_ACCENT, borderColor: TOP_CUSTOMERS_ACCENT, '& .MuiChip-deleteIcon': { color: TOP_CUSTOMERS_ACCENT, opacity: 0.7, '&:hover': { color: TOP_CUSTOMERS_ACCENT, opacity: 1 } } };

// Icon-only action buttons (refresh / download): white fill with a thin border so they stay visible,
// and an explicit keyboard focus ring.
const ICON_BUTTON_SX = {
  bgcolor: '#fff',
  color: DEEP_GREEN,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  '&:hover': { bgcolor: '#fff', filter: 'brightness(0.92)' },
  '&.Mui-focusVisible': { outline: `2px solid ${DEEP_GREEN}`, outlineOffset: 2 },
  '&.Mui-disabled': { bgcolor: '#fff', opacity: 0.5 }
};

// Icon-only button with a CSS-positioned tooltip centred above it. MUI's <Tooltip> (Popper) can't be
// used here: the app runs under `body { zoom: 0.8 }` (main.css), which throws off Popper's measured
// positioning. The wrapper span also keeps hover/focus working while the button is disabled.
// The label is aria-hidden because the button's aria-label already names it for assistive tech.
const ICON_TIP_WRAPPER_SX = {
  position: 'relative',
  display: 'inline-flex',
  '& .icon-tip': {
    position: 'absolute',
    bottom: 'calc(100% + 6px)',
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
    bgcolor: 'rgba(33, 33, 33, 0.92)',
    color: '#fff',
    fontSize: '0.75rem',
    lineHeight: 1.4,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    pointerEvents: 'none',
    zIndex: 1500,
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 0.15s'
  },
  '&:hover .icon-tip, &:focus-within .icon-tip': { visibility: 'visible', opacity: 1 }
};

function IconActionButton({ label, icon, ...buttonProps }) {
  return (
    <Box component="span" sx={ICON_TIP_WRAPPER_SX}>
      <IconButton aria-label={label} size="small" sx={ICON_BUTTON_SX} {...buttonProps}>
        {icon}
      </IconButton>
      <Box component="span" className="icon-tip" aria-hidden="true">{label}</Box>
    </Box>
  );
}

// Every overall_state value DataDog can return — requested in full so the UI can re-filter
// instantly client-side without another round trip to the API.
const ALL_STATUSES = ['OK', 'Alert', 'Warn', 'No Data', 'Skipped', 'Ignored', 'Unknown'];

// Shared true/false options for the Runbook and Notify Targets presence filters.
const BOOLEAN_FILTER_OPTIONS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' }
];

const STATUS_OPTIONS = ALL_STATUSES.map((value) => ({ value, label: value }));
const ALERT_STATE_OPTIONS = ALL_ALERT_STATES.map((value) => ({ value, label: value }));

// Row background for monitors with neither a runbook nor notify targets -- flags the ones
// most likely to go unnoticed/unactioned when they fire.
const NO_DOC_ROW_BG = '#fdecea';

// Cycled per distinct overlap group in the order groups appear in the table (not by raw
// groupId, whose neighbours in the table needn't be consecutive) so adjacent rows from
// different overlap groups never share a color in the Duplicate column.
const DUPLICATE_GROUP_ORANGE = '#e07b00'; // lighter than WARN_ORANGE so the two oranges stay distinguishable
const DUPLICATE_GROUP_COLORS = [WARN_ORANGE, L2_ACCENT, DUPLICATE_GROUP_ORANGE, DEEP_GREEN, INCIDENT_ACCENT];
// keyOf(row) returns the row's group key (or undefined if the row isn't in a group) -- shared
// by the Duplicate column (overlap group id) and the Runbook column (runbook URL).
function buildGroupColors(rows, keyOf) {
  const colors = new Map();
  let prevKey;
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key === undefined) return;
    if (key === prevKey || colors.has(key)) {
      prevKey = key;
      return;
    }
    // Avoid the previous group's color even after the palette wraps around.
    const prevColor = colors.get(prevKey);
    let color = DUPLICATE_GROUP_COLORS[colors.size % DUPLICATE_GROUP_COLORS.length];
    if (color === prevColor) color = DUPLICATE_GROUP_COLORS[(colors.size + 1) % DUPLICATE_GROUP_COLORS.length];
    colors.set(key, color);
    prevKey = key;
  });
  return colors;
}

// Applied to every monitors-table cell so long content wraps within its fixed-width column
// instead of overflowing and visually overlapping the next column.
const WRAP_CELL_SX = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', verticalAlign: 'top' };

// Stays a normal table cell (overriding `display` here breaks the row layout -- every cell
// would stack into column 1 instead of sitting side by side).
const CLAMP_CELL_SX = { verticalAlign: 'top', cursor: 'pointer' };

/** Render monitor rows as CSV text (with header) — exactly the rows currently displayed. */
function monitorsToCsv(rows) {
  const esc = (value) => {
    const s = String(value ?? '').replace(/^[\t\r ]*[=+\-@]|^[\t\r]/, (m) => `'${m}`);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = [
    'Name', 'Status', 'Alert State', 'Creator', 'Notify Targets',
    'Monitored Target', 'Tags', 'Type', 'Last Triggered', 'Modified', 'Created', 'Trigger', 'Runbook'
  ];
  const lines = [header.map(esc).join(',')];
  rows.forEach((row) => {
    lines.push([
      row.name || '', row.status || '', row.alertState || '', row.creator || '(unknown)',
      (row.notifyTargets || []).join(', '), row.monitoredTarget || '', (row.tags || []).join(', '),
      row.type || '', row.lastTriggered || '', row.modified || '', row.created || '', row.trigger || '', row.runbookUrl || ''
    ].map(esc).join(','));
  });
  return lines.join('\r\n') + '\r\n';
}

// Defined at module scope (not inline in render) so Virtuoso always sees the same component
// identities -- recreating these per render would make it remount the virtualized table.
const VirtuosoScroller = React.forwardRef((props, ref) => (
  <TableContainer component={Paper} variant="outlined" ref={ref} {...props} sx={{ borderRadius: '10px' }} />
));
const VirtuosoTable = (props) => <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />;
const VirtuosoTableRow = ({ item, ...props }) => <TableRow {...props} />;
const VirtuosoTableBody = React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />);
const virtuosoComponents = {
  Scroller: VirtuosoScroller,
  Table: VirtuosoTable,
  TableHead,
  TableRow: VirtuosoTableRow,
  TableBody: VirtuosoTableBody
};

/** Filter bar + virtualized monitors table -- drill-down list below the donut cards. */
function MonitorsFilterBarAndTable({
  statusFilter, setStatusFilter,
  alertStateFilter, setAlertStateFilter,
  typeFilter, setTypeFilter, typeOptions,
  hasRunbookFilter, setHasRunbookFilter,
  hasNotifyFilter, setHasNotifyFilter,
  isDuplicateFilter, setIsDuplicateFilter,
  searchTerm, setSearchTerm,
  notifyTargetFilter, setNotifyTargetFilter, runbookUrlFilter, setRunbookUrlFilter,
  triggeredRange, setTriggeredRange,
  filteredMonitors, totalCount,
  overlapGroups, overlapsLoading,
  setTypeRefOpen,
  overlapByMonitorId, setSelectedMonitorDetail, setSelectedGroupId,
  runbookCountByUrl, activeSort,
  listSort, setListSort,
  selectedDonutId, setSelectedDonutId, setDonutTab
}) {
  const duplicateGroupColors = useMemo(
    () => buildGroupColors(filteredMonitors, (row) => overlapByMonitorId.get(row.id)?.groupId),
    [filteredMonitors, overlapByMonitorId]
  );
  // Only runbooks shared by 2+ monitors get a group color.
  const runbookGroupColors = useMemo(
    () => buildGroupColors(filteredMonitors, (row) => (runbookCountByUrl.get(row.runbookUrl) > 1 ? row.runbookUrl : undefined)),
    [filteredMonitors, runbookCountByUrl]
  );
  // Changing a filter by hand ends a tile-driven Creator / Notify Target sort (back to the
  // default), so the header arrow never describes an order the list no longer has. The
  // tile-driven notify-target / runbook filters stay applied (shown as removable chips below)
  // so refining with the dropdowns or Search narrows them instead of silently dropping them.
  const onFilterChange = (setter) => (value) => {
    if (listSort === 'creator' || listSort === 'notify') setListSort('lastTriggered');
    // The dropdowns now own the view: drop the card selection (and its chip) so only the
    // dropdown chips describe what's filtered.
    setSelectedDonutId(null);
    setter(value);
  };
  // Back to the page's default view: all filters cleared, Monitor Definitions tab, All Monitors card.
  // Shared by Show All and the X on the card-driven chips.
  const resetToDefault = () => {
    setTriggeredRange(null);
    setStatusFilter([ALL_FILTER]);
    setAlertStateFilter([ALL_FILTER]);
    setTypeFilter([ALL_FILTER]);
    setHasRunbookFilter([ALL_FILTER]);
    setHasNotifyFilter([ALL_FILTER]);
    setIsDuplicateFilter([ALL_FILTER]);
    setNotifyTargetFilter(null);
    setRunbookUrlFilter(null);
    setSearchTerm('');
    setListSort('lastTriggered');
    setSelectedDonutId('allMonitors');
    setDonutTab(DEFAULT_DONUT_TAB);
  };
  // One outlined chip per selected dropdown value. Removing one only un-selects that value (back to
  // "All" when it was the last), unlike the filled card chips which reset the whole page.
  const dropdownChips = [
    ['Status', statusFilter, setStatusFilter],
    ['Alert State', alertStateFilter, setAlertStateFilter],
    ['Type', typeFilter, setTypeFilter],
    ['Runbook', hasRunbookFilter, setHasRunbookFilter],
    ['Notify Targets', hasNotifyFilter, setHasNotifyFilter],
    ['Duplicate', isDuplicateFilter, setIsDuplicateFilter]
  ].flatMap(([label, selected, setter]) => (isAllFilter(selected) ? [] : selected.map((value) => ({
    key: `${label}:${value}`,
    text: `${label}: ${value}`,
    remove: () => {
      const rest = selected.filter((v) => v !== value);
      onFilterChange(setter)(rest.length ? rest : [ALL_FILTER]);
    }
  }))));
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
        <MultiCheckSelect label="Status" value={statusFilter} onChange={onFilterChange(setStatusFilter)} options={STATUS_OPTIONS} minWidth={160} />
        <MultiCheckSelect label="Alert State" value={alertStateFilter} onChange={onFilterChange(setAlertStateFilter)} options={ALERT_STATE_OPTIONS} minWidth={140} />
        <MultiCheckSelect label="Type" value={typeFilter} onChange={onFilterChange(setTypeFilter)} options={typeOptions} minWidth={180} />
        <MultiCheckSelect label="Runbook" value={hasRunbookFilter} onChange={onFilterChange(setHasRunbookFilter)} options={BOOLEAN_FILTER_OPTIONS} minWidth={120} />
        <MultiCheckSelect label="Notify Targets" value={hasNotifyFilter} onChange={onFilterChange(setHasNotifyFilter)} options={BOOLEAN_FILTER_OPTIONS} minWidth={140} />
        <MultiCheckSelect label="Duplicate" value={isDuplicateFilter} onChange={onFilterChange(setIsDuplicateFilter)} options={BOOLEAN_FILTER_OPTIONS} minWidth={120} />
      </Box>
      {/* Fixed order, top-aligned to the input (40px) so nothing shifts as chips/warnings appear:
          Search, Show All, count, chip, warning. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Name, creator, target, tags, trigger…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          helperText="Search results are based on the current filters."
          sx={{ minWidth: 260 }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={resetToDefault}
          sx={{ textTransform: 'none', height: 40 }}
        >
          Show All
        </Button>
        <Typography sx={{ color: MUTED, fontSize: '0.8125rem', height: 40, display: 'flex', alignItems: 'center' }}>
          {filteredMonitors.length} of {totalCount} monitors
        </Typography>
        <Box sx={{ height: 40, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          {notifyTargetFilter && (
            <Chip size="small" label={`Notify target: ${notifyTargetFilter}`} onDelete={resetToDefault} {...chipRemoveKeys(resetToDefault)} sx={CHIP_SX} />
          )}
          {runbookUrlFilter && (
            <Chip size="small" label={`Runbook: ${runbookUrlFilter}`} onDelete={resetToDefault} {...chipRemoveKeys(resetToDefault)} sx={{ ...CHIP_SX, maxWidth: 360 }} />
          )}
          {triggeredRange && (
            <Chip size="small" label={triggeredRange.chip} onDelete={resetToDefault} {...chipRemoveKeys(resetToDefault)} sx={CHIP_SX} />
          )}
          {/* Any other selected card (the cards above with their own chip are covered already). */}
          {selectedDonutId && !notifyTargetFilter && !runbookUrlFilter && !triggeredRange && (
            <Chip
              size="small"
              label={selectedDonutLabel(selectedDonutId)}
              onDelete={selectedDonutId === 'allMonitors' ? undefined : resetToDefault}
              {...chipRemoveKeys(selectedDonutId === 'allMonitors' ? undefined : resetToDefault)}
              sx={CHIP_SX}
            />
          )}
          {dropdownChips.map((c) => (
            <Chip key={c.key} size="small" variant="outlined" label={c.text} onDelete={c.remove} {...chipRemoveKeys(c.remove)} sx={OUTLINED_CHIP_SX} />
          ))}
        </Box>
        {overlapGroups === null && !isAllFilter(isDuplicateFilter) && (
          <Typography sx={{ color: WARN_ORANGE, fontSize: '0.8125rem', height: 40, display: 'flex', alignItems: 'center' }}>
            {overlapsLoading ? 'Duplicate filter pending overlap detection…' : 'Duplicate filter unavailable (overlap detection failed)'}
          </Typography>
        )}
      </Box>

      <TableVirtuoso
        style={{ height: 560 }}
        data={filteredMonitors}
        computeItemKey={(_index, row) => row.id}
        components={virtuosoComponents}
        fixedHeaderContent={() => (
          <TableRow sx={{ bgcolor: '#fff' }}>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '22ch' }}>Name</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '8ch' }}>Status</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '9ch' }}>Alert State</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '9ch' }}>Duplicate</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '12ch' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Creator
                {activeSort === 'creator' && <ArrowDownIcon fontSize="small" aria-label="Sorted by creator" />}
              </Box>
            </TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '14ch' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Notify Targets
                {activeSort === 'notify' && <ArrowDownIcon fontSize="small" aria-label="Sorted by notify target" />}
              </Box>
            </TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '24ch' }}>Monitored Target</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '16ch' }}>Tags</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '14ch' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Type
                <IconButton size="small" onClick={() => setTypeRefOpen(true)} aria-label="Monitor type reference" sx={{ p: 0.25, ml: 0.25 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Box>
            </TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '14ch' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Last Triggered
                {activeSort === 'lastTriggered' && <ArrowDownIcon fontSize="small" aria-label="Sorted by last triggered, newest first" />}
              </Box>
            </TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '14ch' }}>Modified</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '14ch' }}>Created</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '27ch' }}>Trigger</TableCell>
            <TableCell sx={{ ...WRAP_CELL_SX, width: '14ch' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                Runbook
              </Box>
            </TableCell>
          </TableRow>
        )}
        itemContent={(_index, row) => {
          const overlap = overlapByMonitorId.get(row.id);
          const alertStateStyle = ALERT_STATE_STYLES[row.alertState] || { color: MUTED, bg: 'transparent' };
          const openDetail = () => setSelectedMonitorDetail(row);
          const eventsUrl = monitorEventsUrl(row);
          // Applied per-cell (not on the row) -- the theme's zebra-stripe CSS targets
          // .MuiTableRow-root:nth-of-type(even) with higher specificity than a row-level
          // sx override, so every other matching row would otherwise lose its highlight.
          const rowBg = docCategory(row) === 'No Notify or Runbook' ? NO_DOC_ROW_BG : undefined;
          const cellSx = (base) => (rowBg ? { ...base, bgcolor: rowBg } : base);
          return (
            <>
              <TableCell sx={cellSx({ ...CLAMP_CELL_SX, cursor: 'default' })}>
                <Box sx={CLAMP_INNER_SX}>
                  {row.url ? (
                    <Link href={row.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {row.name} <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ) : row.name}
                </Box>
              </TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)}>
                <ButtonBase
                  onClick={openDetail}
                  aria-label={`Open details for ${row.name || 'monitor'}`}
                  sx={{ ...CLAMP_INNER_SX, justifyContent: 'flex-start', width: '100%', textAlign: 'left', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
                >
                  <Box component="span" sx={{ color: STATUS_COLORS[row.status] || MUTED, fontWeight: 700 }}>{row.status}</Box>
                </ButtonBase>
              </TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}>
                <Box sx={CLAMP_INNER_SX}>
                  <Box
                    component="span"
                    sx={{
                      px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.6875rem', fontWeight: 700,
                      color: alertStateStyle.color, bgcolor: alertStateStyle.bg
                    }}
                  >
                    {row.alertState}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}>
                {overlap ? (
                  <Chip
                    size="small"
                    label={overlap.size}
                    onClick={(e) => { e.stopPropagation(); setSelectedGroupId(overlap.groupId); }}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: duplicateGroupColors.get(overlap.groupId),
                      color: '#fff',
                      fontWeight: 700
                    }}
                  />
                ) : '—'}
              </TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}><Box sx={CLAMP_INNER_SX}>{row.creator || '(unknown)'}</Box></TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}><Box sx={CLAMP_INNER_SX}>{(row.notifyTargets || []).join(', ')}</Box></TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}><Box sx={CLAMP_INNER_SX}>{row.monitoredTarget}</Box></TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}><Box sx={CLAMP_INNER_SX}>{(row.tags || []).join(', ')}</Box></TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}><Box sx={CLAMP_INNER_SX}>{row.type}</Box></TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}>
                <Box sx={CLAMP_INNER_SX}>
                  {eventsUrl ? (
                    // stopPropagation: the cell's onClick opens the detail dialog, not this link.
                    <Link href={eventsUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {new Date(row.lastTriggered).toLocaleString()} <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ) : '—'}
                </Box>
              </TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}>
                <Box sx={CLAMP_INNER_SX}>{row.modified ? new Date(row.modified).toLocaleString() : '—'}</Box>
              </TableCell>
              <TableCell sx={cellSx(CLAMP_CELL_SX)} onClick={openDetail}>
                <Box sx={CLAMP_INNER_SX}>{row.created ? new Date(row.created).toLocaleString() : '—'}</Box>
              </TableCell>
              <TableCell sx={cellSx({ ...CLAMP_CELL_SX, fontFamily: 'monospace', fontSize: '0.75rem' })} onClick={openDetail}>
                <Box sx={CLAMP_INNER_SX}>{row.trigger}</Box>
              </TableCell>
              <TableCell sx={cellSx({ ...CLAMP_CELL_SX, cursor: row.runbookUrl ? 'pointer' : 'default' })}>
                {row.runbookUrl ? (
                  <Link
                    href={row.runbookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open runbook for ${row.name || 'monitor'}`}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, width: '100%', height: '100%' }}
                  >
                    <Chip
                      size="small"
                      label={runbookCountByUrl.get(row.runbookUrl) || 1}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: runbookGroupColors.get(row.runbookUrl) || NEUTRAL_SURFACE,
                        color: runbookGroupColors.has(row.runbookUrl) ? '#fff' : INK,
                        fontWeight: 700
                      }}
                    />
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </Link>
                ) : '—'}
              </TableCell>
            </>
          );
        }}
      />
    </>
  );
}

const DatadogMonitorsPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Set when a refresh failed but the previously stored monitors are still being shown.
  const [refreshWarning, setRefreshWarning] = useState('');
  const [monitors, setMonitors] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [statusFilter, setStatusFilter] = useState([ALL_FILTER]);
  const [alertStateFilter, setAlertStateFilter] = useState([ALL_FILTER]);
  const [typeFilter, setTypeFilter] = useState([ALL_FILTER]);
  const [hasRunbookFilter, setHasRunbookFilter] = useState([ALL_FILTER]);
  const [hasNotifyFilter, setHasNotifyFilter] = useState([ALL_FILTER]);
  const [isDuplicateFilter, setIsDuplicateFilter] = useState([ALL_FILTER]);
  const [searchTerm, setSearchTerm] = useState('');
  const [notifyTargetFilter, setNotifyTargetFilter] = useState(null);
  const [runbookUrlFilter, setRunbookUrlFilter] = useState(null);
  // The TRIGGERED_RANGES entry for the "last triggered" age filter (set by the Triggered Info donuts), or null.
  const [triggeredRange, setTriggeredRange] = useState(null);

  const [overlapsLoading, setOverlapsLoading] = useState(false);
  const [overlapsError, setOverlapsError] = useState('');
  const [overlapGroups, setOverlapGroups] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [typeRefOpen, setTypeRefOpen] = useState(false);
  const [selectedMonitorDetail, setSelectedMonitorDetail] = useState(null);
  // Column the list is sorted by ('lastTriggered' | 'creator' | 'notify' | null). Defaults to
  // every monitor, most recently triggered first; the Unique Creators / Notify Targets tiles
  // switch it, and other filter-driving actions clear it.
  const [listSort, setListSort] = useState('lastTriggered');
  // Which donut-card popup is open: 'duplicates' | 'runbooks' | 'creators' | 'notifyGroups' | 'recentlyNotified' | null.
  const [activePopup, setActivePopup] = useState(null);
  // Find Overlaps always runs right after the monitors list loads (initial load and every
  // "Refresh from DataDog") -- there's no separate button for it.
  const overlapRequestIdRef = useRef(0);
  const findOverlaps = useCallback(async () => {
    const requestId = (overlapRequestIdRef.current += 1);
    setOverlapsLoading(true);
    setOverlapsError('');
    // Clear the previous snapshot's groups/selection immediately: the monitor rows are about
    // to be replaced, so stale duplicate chips/dialogs must not linger if this request is
    // slow or fails.
    setOverlapGroups(null);
    setSelectedGroupId(null);
    try {
      const data = await authGet('/api/datadog-monitors/overlaps', null, 'json', { useCache: false });
      // A newer refresh may have started and finished first; don't let this stale response
      // overwrite its (more current) results.
      if (overlapRequestIdRef.current !== requestId) return;
      setOverlapGroups(data.groups || []);
    } catch (err) {
      if (overlapRequestIdRef.current !== requestId) return;
      setOverlapsError(err.message || 'Failed to find overlapping monitors');
    } finally {
      if (overlapRequestIdRef.current === requestId) setOverlapsLoading(false);
    }
  }, []);

  const loadMonitorsRequestIdRef = useRef(0);
  const loadMonitors = useCallback(async (forceRefresh) => {
    const requestId = (loadMonitorsRequestIdRef.current += 1);
    setLoading(true);
    setError('');
    setRefreshWarning('');
    try {
      const data = await authGet('/api/datadog-monitors/monitors', {
        statuses: ALL_STATUSES.join(','),
        forceRefresh: forceRefresh ? 'true' : undefined
      }, 'json', { useCache: false });
      // A slower load (e.g. the initial mount fetch) can finish after a newer one (e.g. a
      // quick manual Refresh click); don't let it overwrite the newer, more current result.
      if (loadMonitorsRequestIdRef.current !== requestId) return;
      setMonitors(data.monitors || []);
      setFetchedAt(data.fetchedAt || null);
      setRefreshWarning(data.refreshError || '');
      setHasLoaded(true);
      findOverlaps();
    } catch (err) {
      if (loadMonitorsRequestIdRef.current !== requestId) return;
      setError(err.message || 'Failed to load DataDog monitors');
    } finally {
      if (loadMonitorsRequestIdRef.current === requestId) setLoading(false);
    }
  }, [findOverlaps]);

  useEffect(() => {
    loadMonitors(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drives the donut cards -- clicking one sets these filters to isolate exactly the monitors
  // it counted (any omitted dimension resets
  // to "All", and search is always cleared).
  const applySummaryFilter = useCallback(({ status, alertState, hasRunbook, hasNotify, isDuplicate, triggeredRange: range } = {}) => {
    setTriggeredRange(range ?? null);
    setStatusFilter(status ? [status] : [ALL_FILTER]);
    setAlertStateFilter(alertState ? [alertState] : [ALL_FILTER]);
    setTypeFilter([ALL_FILTER]);
    setHasRunbookFilter(hasRunbook === undefined ? [ALL_FILTER] : [String(hasRunbook)]);
    setHasNotifyFilter(hasNotify === undefined ? [ALL_FILTER] : [String(hasNotify)]);
    setIsDuplicateFilter(isDuplicate === undefined ? [ALL_FILTER] : [String(isDuplicate)]);
    setNotifyTargetFilter(null);
    setRunbookUrlFilter(null);
    setSearchTerm('');
    setListSort('lastTriggered');
  }, []);

  // Clicking a donut card isolates the monitors it counted, optionally re-sorts the list, and
  // optionally opens that card's popup (see DONUT_ROWS).
  const [selectedDonutId, setSelectedDonutId] = useState('allMonitors');
  const [donutTab, setDonutTab] = useState(DEFAULT_DONUT_TAB);
  const handleDonutSelect = useCallback((card) => {
    setSelectedDonutId(card.id);
    applySummaryFilter(card.filter);
    if (card.sort) setListSort(card.sort);
    if (card.popup) setActivePopup(card.popup);
  }, [applySummaryFilter]);

  // monitor id -> { groupId, size } so the Duplicate column matches the Find Overlaps results.
  const overlapByMonitorId = useMemo(() => {
    const map = new Map();
    (overlapGroups || []).forEach((group) => {
      group.monitors.forEach((m) => map.set(m.id, { groupId: group.groupId, size: group.monitors.length }));
    });
    return map;
  }, [overlapGroups]);

  const overlapGroupsById = useMemo(() => {
    const map = new Map();
    (overlapGroups || []).forEach((group) => map.set(group.groupId, group));
    return map;
  }, [overlapGroups]);

  const typeOptions = useMemo(() => {
    const types = [...new Set(monitors.map((m) => m.type).filter(Boolean))].sort();
    return types.map((value) => ({ value, label: value }));
  }, [monitors]);

  // The sort actually applied to the list (drives both the row order and the header arrow).
  // Creator / Notify Target sorts (set by their tiles) win; otherwise Duplicate=true keeps its
  // group ordering and Runbook=true groups by runbook URL -- including when set by hand from the
  // dropdowns -- over the default Last Triggered order.
  const duplicatesOnly = overlapGroups !== null && isDuplicateFilter.length === 1 && isDuplicateFilter[0] === 'true';
  let activeSort = listSort;
  if (listSort !== 'creator' && listSort !== 'notify') {
    if (duplicatesOnly) activeSort = 'duplicates';
  }

  // runbook URL -> number of monitors (any alert state) tied to it.
  const runbookCountByUrl = useMemo(() => {
    const map = new Map();
    monitors.forEach((m) => {
      if (m.runbookUrl) map.set(m.runbookUrl, (map.get(m.runbookUrl) || 0) + 1);
    });
    return map;
  }, [monitors]);

  const filteredMonitors = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    // overlapGroups is null until the first /overlaps response lands (or if it failed) --
    // overlapByMonitorId is an empty Map in that window, which would make every monitor look
    // "not a duplicate" and silently show 0 rows for Duplicate=true. Don't apply this filter
    // until overlap detection has actually produced a result.
    const overlapsReady = overlapGroups !== null;
    const rows = monitors.filter((m) => {
      if (!filterAllows(statusFilter, m.status)) return false;
      if (!filterAllows(alertStateFilter, m.alertState)) return false;
      if (!filterAllows(typeFilter, m.type)) return false;
      if (!filterAllows(hasRunbookFilter, String(!!m.runbookUrl))) return false;
      if (!filterAllows(hasNotifyFilter, String((m.notifyTargets || []).length > 0))) return false;
      if (overlapsReady && !filterAllows(isDuplicateFilter, String(overlapByMonitorId.has(m.id)))) return false;
      if (notifyTargetFilter && !(m.notifyTargets || []).includes(notifyTargetFilter)) return false;
      if (runbookUrlFilter && m.runbookUrl !== runbookUrlFilter) return false;
      if (triggeredRange && !triggeredInRange(m, triggeredRange)) return false;
      if (!needle) return true;
      const haystack = [m.name, m.creator, m.monitoredTarget, (m.tags || []).join(' '), (m.notifyTargets || []).join(' '), m.trigger, m.runbookUrl]
        .join('\u0000')
        .toLowerCase();
      return haystack.includes(needle);
    });
    // Duplicate-only view (from the Duplicates donut or the Duplicate filter) -- sort by
    // overlap group size (biggest clusters first), then most recently triggered, then group
    // number, instead of the default monitors[] order. Group-level keys must all precede the
    // per-monitor key, otherwise same-size groups interleave and members aren't adjacent.
    if (activeSort === 'creator') {
      // Monitors with no creator sort last.
      rows.sort((a, b) => {
        const ca = a.creator || '';
        const cb = b.creator || '';
        if (!ca !== !cb) return ca ? -1 : 1;
        return ca.localeCompare(cb) || (a.name || '').localeCompare(b.name || '');
      });
    } else if (activeSort === 'lastTriggered') {
      // Newest first; monitors that never triggered go last.
      const triggeredMs = (m) => (m.lastTriggered ? new Date(m.lastTriggered).getTime() : -Infinity);
      const createdMs = (m) => (m.created ? new Date(m.created).getTime() : -Infinity);
      rows.sort((a, b) => compareNewestFirst(triggeredMs(a), triggeredMs(b)) || compareNewestFirst(createdMs(a), createdMs(b)) || (a.name || '').localeCompare(b.name || ''));
    } else if (activeSort === 'notify') {
      // Monitors with no notify targets sort last.
      rows.sort((a, b) => {
        const ka = notifyGroupKey(a);
        const kb = notifyGroupKey(b);
        if (!ka !== !kb) return ka ? -1 : 1;
        return ka.localeCompare(kb) || (a.name || '').localeCompare(b.name || '');
      });
    }
    if (activeSort === 'triggerInfo') {
      // Most recent first, but a duplicate group moves as one block ranked by its newest visible
      // member (so exact timestamps don't split it), then group, alert state, alert status, name.
      const triggeredMs = (m) => (m.lastTriggered ? new Date(m.lastTriggered).getTime() : -Infinity);
      const groupLatest = new Map();
      rows.forEach((m) => {
        const groupId = overlapByMonitorId.get(m.id)?.groupId;
        if (groupId !== undefined) groupLatest.set(groupId, Math.max(groupLatest.get(groupId) ?? -Infinity, triggeredMs(m)));
      });
      const rank = (m) => {
        const groupId = overlapByMonitorId.get(m.id)?.groupId;
        return groupId === undefined ? triggeredMs(m) : groupLatest.get(groupId);
      };
      const text = (v) => (v || '');
      rows.sort((a, b) => compareNewestFirst(rank(a), rank(b))
        || (overlapByMonitorId.get(a.id)?.groupId ?? -1) - (overlapByMonitorId.get(b.id)?.groupId ?? -1)
        || text(a.alertState).localeCompare(text(b.alertState))
        || text(a.status).localeCompare(text(b.status))
        || text(a.name).localeCompare(text(b.name)));
    } else if (activeSort === 'recentlyRun' || activeSort === 'recentlyNotified') {
      // Most recent first, but monitors sharing a runbook (or notify-target set) move as one block
      // ranked by the newest visible member (so exact timestamps don't split it), then that key,
      // alert state, status, created (newest first), name.
      const triggeredMs = (m) => (m.lastTriggered ? new Date(m.lastTriggered).getTime() : -Infinity);
      const createdMs = (m) => (m.created ? new Date(m.created).getTime() : -Infinity);
      const blockKey = activeSort === 'recentlyRun' ? (m) => m.runbookUrl || '' : notifyGroupKey;
      const blockLatest = new Map();
      rows.forEach((m) => {
        const key = blockKey(m);
        if (key) blockLatest.set(key, Math.max(blockLatest.get(key) ?? -Infinity, triggeredMs(m)));
      });
      const rank = (m) => (blockKey(m) ? blockLatest.get(blockKey(m)) : triggeredMs(m));
      const text = (v) => (v || '');
      rows.sort((a, b) => compareNewestFirst(rank(a), rank(b))
        || blockKey(a).localeCompare(blockKey(b))
        || text(a.alertState).localeCompare(text(b.alertState))
        || text(a.status).localeCompare(text(b.status))
        || compareNewestFirst(createdMs(a), createdMs(b))
        || text(a.name).localeCompare(text(b.name)));
    } else if (activeSort === 'duplicates') {
      const triggeredMs = (m) => (m.lastTriggered ? new Date(m.lastTriggered).getTime() : -Infinity);
      const groupLatest = new Map();
      // Only members that survived the filters count, so group order matches the visible rows.
      rows.forEach((m) => {
        const groupId = overlapByMonitorId.get(m.id)?.groupId;
        if (groupId !== undefined) groupLatest.set(groupId, Math.max(groupLatest.get(groupId) ?? -Infinity, triggeredMs(m)));
      });
      rows.sort((a, b) => {
        const oa = overlapByMonitorId.get(a.id);
        const ob = overlapByMonitorId.get(b.id);
        const sizeDiff = (ob?.size || 0) - (oa?.size || 0);
        if (sizeDiff !== 0) return sizeDiff;
        if (oa?.groupId !== ob?.groupId) {
          const recentDiff = (groupLatest.get(ob?.groupId) ?? -Infinity) - (groupLatest.get(oa?.groupId) ?? -Infinity);
          if (recentDiff !== 0 && !Number.isNaN(recentDiff)) return recentDiff;
          return (oa?.groupId ?? 0) - (ob?.groupId ?? 0);
        }
        return compareNewestFirst(triggeredMs(a), triggeredMs(b));
      });
    }
    return rows;
  }, [monitors, statusFilter, alertStateFilter, typeFilter, hasRunbookFilter, hasNotifyFilter, isDuplicateFilter, overlapByMonitorId, overlapGroups, notifyTargetFilter, runbookUrlFilter, triggeredRange, searchTerm, activeSort]);

  const stats = useMonitorStats(monitors, overlapGroups, overlapByMonitorId);

  const handleDownloadCsv = useCallback(() => {
    const blob = new Blob([monitorsToCsv(filteredMonitors)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'datadog_monitors.csv';
    link.click();
    // Some browsers start the download asynchronously; revoking right away can cancel it.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [filteredMonitors]);

  const closePopup = useCallback(() => setActivePopup(null), []);
  const selectedGroup = selectedGroupId !== null ? overlapGroupsById.get(selectedGroupId) : null;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: INK }}>DataDog Monitors</Typography>
      <Typography sx={{ color: MUTED, fontSize: '0.875rem', mt: 0.5, mb: 2 }}>
        List, filter, and export monitors. Overlapping monitors are detected automatically.
      </Typography>

      {/* Last fetched stamp with the refresh / export actions to its right, above the tabs. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        {fetchedAt && (
          <Typography sx={{ color: INK, fontSize: '1.125rem', fontWeight: 600, mr: 1 }}>
            Last fetched {new Date(fetchedAt).toLocaleString()}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconActionButton
            label="Refresh from DataDog"
            icon={<RefreshIcon fontSize="small" />}
            // Also disabled during overlap detection (not just the monitors fetch): findOverlaps
            // keeps running after loading flips back to false, and re-clicking mid-overlap-fetch
            // would start a second concurrent forced refresh from this same browser session.
            disabled={loading || overlapsLoading}
            onClick={() => loadMonitors(true)}
          />
          <IconActionButton
            label="Download CSV file"
            icon={<DownloadIcon fontSize="small" />}
            disabled={!filteredMonitors.length}
            onClick={handleDownloadCsv}
          />
          {loading && <CircularProgress size={16} />}
        </Box>
      </Box>

      {hasLoaded && (
        <Box sx={{ mb: 2 }}>
          <DonutCardRows
            rows={DONUT_ROWS} stats={stats} onSelect={handleDonutSelect} selectedId={selectedDonutId}
            tabIndex={donutTab} onTabChange={setDonutTab}
          />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, maxWidth: 720 }}>{error}</Alert>}
      {refreshWarning && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 720 }}>
          Refresh from DataDog failed: {refreshWarning} Showing the previously stored monitors.
        </Alert>
      )}
      {overlapsError && <Alert severity="error" sx={{ mb: 2, maxWidth: 720 }}>{overlapsError}</Alert>}

      {hasLoaded && (
        <>
          <MonitorsFilterBarAndTable
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              alertStateFilter={alertStateFilter} setAlertStateFilter={setAlertStateFilter}
              typeFilter={typeFilter} setTypeFilter={setTypeFilter} typeOptions={typeOptions}
              hasRunbookFilter={hasRunbookFilter} setHasRunbookFilter={setHasRunbookFilter}
              hasNotifyFilter={hasNotifyFilter} setHasNotifyFilter={setHasNotifyFilter}
              isDuplicateFilter={isDuplicateFilter} setIsDuplicateFilter={setIsDuplicateFilter}
              searchTerm={searchTerm} setSearchTerm={setSearchTerm}
              notifyTargetFilter={notifyTargetFilter} setNotifyTargetFilter={setNotifyTargetFilter} runbookUrlFilter={runbookUrlFilter} setRunbookUrlFilter={setRunbookUrlFilter}
              triggeredRange={triggeredRange} setTriggeredRange={setTriggeredRange}
              filteredMonitors={filteredMonitors} totalCount={monitors.length}
              overlapGroups={overlapGroups} overlapsLoading={overlapsLoading}
              setTypeRefOpen={setTypeRefOpen}
              overlapByMonitorId={overlapByMonitorId} setSelectedMonitorDetail={setSelectedMonitorDetail} setSelectedGroupId={setSelectedGroupId}
              runbookCountByUrl={runbookCountByUrl} activeSort={activeSort}
              listSort={listSort} setListSort={setListSort}
              selectedDonutId={selectedDonutId} setSelectedDonutId={setSelectedDonutId} setDonutTab={setDonutTab}
            />
        </>
      )}

      <TypeReferencePopup open={typeRefOpen} onClose={() => setTypeRefOpen(false)} />
      <OverlapGroupPopup group={selectedGroup} onClose={() => setSelectedGroupId(null)} />
      <MonitorDetailPopup monitor={selectedMonitorDetail} onClose={() => setSelectedMonitorDetail(null)} />
      <DuplicateGroupsPopup open={activePopup === 'duplicates'} onClose={closePopup} groups={stats.duplicateGroups} />
      <RunbooksPopup open={activePopup === 'runbooks'} onClose={closePopup} runbooks={stats.runbooks} />
      <CreatorsPopup
        open={activePopup === 'creators'} onClose={closePopup}
        monitors={monitors} creators={stats.creators} stats={stats.creatorStats}
      />
      <RecentlyNotifiedPopup
        open={activePopup === 'recentlyNotified'} onClose={closePopup}
        monitors={monitors} range={TRIGGERED_RANGES.find((r) => `notify_${r.id}` === selectedDonutId)}
      />
      <NotifyGroupsPopup
        open={activePopup === 'notifyGroups'} onClose={closePopup}
        notifyGroups={stats.notifyGroups} individualTargets={stats.individualNotifyTargets} stats={stats.notifyGroupStats}
      />
    </Box>
  );
};

export default DatadogMonitorsPage;
